import type {
  IConversationInboxPreferences,
  AgentOpensInType,
  AgentUsageChartExtraSeriesType,
  IAgentOpeningPreferences,
  IActivityLastVisitPreferences,
  IAgentUsageChartPreferences,
  IItemOpeningTabPreferences,
  IInvitationsSeenPreferences,
  ItemOpeningTabType,
  INotificationPreferences,
  IPhonePreferences,
  PhonePreferencesSaveType,
  IToolbarPreferences,
  IViewsOverviewPreferences,
  ViewsOverviewDisplayType,
  ViewsOverviewGroupingType,
  ViewsOverviewSortType,
  IWorkAreaNavigationPreferences,
  IWorkAreaOverviewPreferences,
} from '@featit/shared';
import { ApiService } from './ApiService';
import { forgetSharedRead, sharedRead } from './sharedRead';
import { expectRecord } from './answerShape';

class UserPreferencesApiService extends ApiService {
  /** F-0383, §10.9 NV-4/NV-5: an omitted `namespaceId` reads the unscoped "all" row, unchanged. */
  workAreaOverview(namespaceId?: string | null): Promise<IWorkAreaOverviewPreferences> {
    return this.get('/user-preferences/work-area-overview', {
      params: namespaceId ? { namespaceId } : undefined,
    });
  }

  updateWorkAreaOverview(
    preferences: IWorkAreaOverviewPreferences,
    namespaceId?: string | null,
  ): Promise<IWorkAreaOverviewPreferences> {
    return this.patch('/user-preferences/work-area-overview', {
      expectedRevision: preferences.revision,
      blocks: preferences.blocks,
      views: preferences.views,
      ...(namespaceId ? { namespaceId } : null),
    });
  }

  workAreaNavigation(): Promise<IWorkAreaNavigationPreferences> {
    // T-0559: every mounted reader of the order shares one read.
    return sharedRead('user-preferences/work-area-navigation', () =>
      this.get('/user-preferences/work-area-navigation'),
    );
  }

  updateWorkAreaNavigation(
    preferences: IWorkAreaNavigationPreferences,
  ): Promise<IWorkAreaNavigationPreferences> {
    forgetSharedRead('user-preferences/work-area-navigation');
    return this.patch('/user-preferences/work-area-navigation', {
      expectedRevision: preferences.revision,
      workAreaOrder: preferences.workAreaOrder,
      namespaceOrder: preferences.namespaceOrder,
    });
  }

  agentUsageChart(): Promise<IAgentUsageChartPreferences> {
    return this.get('/user-preferences/agent-usage-chart');
  }

  /** The whole set of switched-on types; last write wins (concept-agent-token-usage §12.2). */
  updateAgentUsageChart(
    series: AgentUsageChartExtraSeriesType[],
  ): Promise<IAgentUsageChartPreferences> {
    return this.patch('/user-preferences/agent-usage-chart', { series });
  }

  async notifications(): Promise<INotificationPreferences> {
    return expectRecord<INotificationPreferences>(
      await this.get('/user-preferences/notifications'),
      'Notification settings',
      ['revision', 'general'],
    );
  }

  /**
   * Sends the whole set, not the part that changed.
   *
   * The two settings screens edit different slices of one blob — the general grid and one work
   * area's override — and a partial write from either would have to be merged with whatever is
   * stored, which is the merge this feature refuses everywhere else. `revision` is what makes that
   * safe: the endpoint rejects a write built on a set somebody else has since replaced rather than
   * silently discarding their edit.
   */
  updateNotifications(preferences: INotificationPreferences): Promise<INotificationPreferences> {
    return this.patch('/user-preferences/notifications', {
      expectedRevision: preferences.revision,
      general: preferences.general,
      workAreas: preferences.workAreas,
      // F-0344: the phone column, the idle rule and the profiles travel with the grid.
      push: preferences.push,
      agentIdle: preferences.agentIdle,
      // F-0815 UTN-2: the urgent-task raise window.
      taskUrgent: preferences.taskUrgent,
      profiles: preferences.profiles,
    });
  }

  toolbar(): Promise<IToolbarPreferences> {
    // T-0559: every mounted reader of the toolbar shares one read.
    return sharedRead('user-preferences/toolbar', () => this.get('/user-preferences/toolbar'));
  }

  /** F-0343 AC-3: the server round trip the mutation test proves — see `useToolbarPreferences`. */
  updateToolbar(preferences: IToolbarPreferences): Promise<IToolbarPreferences> {
    forgetSharedRead('user-preferences/toolbar');
    return this.patch('/user-preferences/toolbar', {
      expectedRevision: preferences.revision,
      order: preferences.order,
      // F-0562: the descriptor of every view, page or work-area destination the order names.
      places: preferences.places ?? [],
      // F-0414: `undefined` is dropped from the body, and the server then keeps what it has.
      recent: preferences.recent,
      recentPinned: preferences.recentPinned,
    });
  }

  /** IU-12: the "New since your last visit" divider's bookmark, one per work area. */
  activityLastVisit(workAreaId: string): Promise<IActivityLastVisitPreferences> {
    return this.get('/user-preferences/activity-last-visit', { params: { workAreaId } });
  }

  updateActivityLastVisit(
    workAreaId: string,
    visitedAt: string,
  ): Promise<IActivityLastVisitPreferences> {
    return this.patch('/user-preferences/activity-last-visit', { workAreaId, visitedAt });
  }

  /** HP-50 (F-0443): the reader's own opening tab per item type, over the work area's. */
  itemOpeningTab(): Promise<IItemOpeningTabPreferences> {
    return this.get('/user-preferences/item-opening-tab');
  }

  /** Names only the types that change; `null` clears one, so the work area's tab applies again. */
  updateItemOpeningTab(
    expectedRevision: number,
    tabs: Record<string, ItemOpeningTabType | null>,
  ): Promise<IItemOpeningTabPreferences> {
    return this.patch('/user-preferences/item-opening-tab', { expectedRevision, tabs });
  }

  /** F-0646 AC-CV.60: *Open agents in*, the caller's own choice of conversation or terminal. */
  agentOpening(): Promise<IAgentOpeningPreferences> {
    return this.get('/user-preferences/agent-opening');
  }

  updateAgentOpening(opensIn: AgentOpensInType): Promise<IAgentOpeningPreferences> {
    return this.patch('/user-preferences/agent-opening', { opensIn });
  }

  /** F-0719 (AC-CV.256, AC-CV.257): how the person's conversations inbox is ordered and grouped. */
  conversationInbox(): Promise<IConversationInboxPreferences> {
    return this.get('/user-preferences/conversation-inbox');
  }

  /** The sort, the grouping or both; what is left out is kept. */
  updateConversationInbox(
    change: Partial<IConversationInboxPreferences>,
  ): Promise<IConversationInboxPreferences> {
    return this.patch('/user-preferences/conversation-inbox', change);
  }

  phone(): Promise<IPhonePreferences> {
    // T-0559: every mounted reader of the phone preferences shares one read (gate 474 sweep: two
    // readers asked twice on a cold launch).
    return sharedRead('user-preferences/phone', () => this.get('/user-preferences/phone'));
  }

  /**
   * IU-4 (F-0507): omitted fields are dropped from the body, and the server keeps what it has
   * stored for them — the same "send only what changed" shape `updateToolbar` uses.
   */
  updatePhone(preferences: PhonePreferencesSaveType) {
    forgetSharedRead('user-preferences/phone');
    return this.patch<IPhonePreferences>('/user-preferences/phone', preferences);
  }

  /** F-0549: the Views overview's display, grouping and favourites, kept per person. */
  viewsOverview(): Promise<IViewsOverviewPreferences> {
    return sharedRead('user-preferences/views-overview', () =>
      this.get('/user-preferences/views-overview'),
    );
  }

  /** Only what changed is sent; the server keeps the rest, and never takes favourites here. */
  updateViewsOverview(settings: {
    expectedRevision: number;
    display?: ViewsOverviewDisplayType;
    grouping?: ViewsOverviewGroupingType;
    sort?: ViewsOverviewSortType;
    favouritesFirst?: boolean;
  }): Promise<IViewsOverviewPreferences> {
    forgetSharedRead('user-preferences/views-overview');
    return this.patch('/user-preferences/views-overview', settings);
  }

  /** VO-9: a star, idempotent. A built-in's id (`default-view:roadmap`) is encoded into the path. */
  setViewsOverviewFavourite(
    workAreaId: string,
    viewId: string,
    favourite: boolean,
  ): Promise<IViewsOverviewPreferences> {
    forgetSharedRead('user-preferences/views-overview');
    const path = `/user-preferences/views-overview/favourites/${encodeURIComponent(workAreaId)}/${encodeURIComponent(viewId)}`;
    return favourite ? this.put(path, {}) : this.delete(path);
  }

  /**
   * VO-21: this person opened a view. The server stamps and dedupes it; the read is not forgotten
   * here, as the answer is the new record and the model takes it from there.
   */
  recordViewsOverviewOpen(workAreaId: string, viewId: string): Promise<IViewsOverviewPreferences> {
    forgetSharedRead('user-preferences/views-overview');
    return this.put(
      `/user-preferences/views-overview/opened/${encodeURIComponent(workAreaId)}/${encodeURIComponent(viewId)}`,
      {},
    );
  }

  /** I-2233 (AC-PI.5): the invitations whose waiting notice this person has been shown. */
  invitationsSeen(): Promise<IInvitationsSeenPreferences> {
    return this.get('/user-preferences/invitations-seen');
  }

  /** I-2233: shown once — idempotent on the server. */
  markInvitationSeen(invitationId: string): Promise<IInvitationsSeenPreferences> {
    return this.put(`/user-preferences/invitations-seen/${encodeURIComponent(invitationId)}`, {});
  }
}

export const userPreferencesApi = new UserPreferencesApiService();
